// TEMPORARY on-device checker (2026-09-27) for "the whole page slides sideways on iPhone".
// Off for everyone. Switched on per device with ?overflowcheck=1 on any app URL (remembered on that
// device), off again with ?overflowcheck=0. While on, every ~1.5s it outlines in red each element
// that sticks out past the screen edge — the thing that makes the page wider than the phone — and
// lists them (with how many px too wide) in a small box at the top. Elements that overflow INSIDE
// something built to scroll or clip sideways (tables, tab bars, the tally grid) are ignored: that
// overflow is intended. Remove this file and its one call in app.component once the culprits are
// fixed.

const KEY = 'llOverflowCheck';

function enabled(): boolean {
  try {
    const q = new URLSearchParams(location.search).get('overflowcheck');
    if (q === '1') localStorage.setItem(KEY, '1');
    if (q === '0') localStorage.removeItem(KEY);
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

/** True if a non-root ancestor already contains this element's overflow (scrolls or clips it)
 *  and that ancestor itself fits on screen — then it can't widen the page. */
function containedByAncestor(el: HTMLElement, vw: number): boolean {
  for (let a = el.parentElement; a && a.tagName !== 'APP-ROOT' && a !== document.body; a = a.parentElement) {
    const ox = getComputedStyle(a).overflowX;
    if (ox !== 'visible') {
      const r = a.getBoundingClientRect();
      if (r.left >= -1 && r.right <= vw + 1) return true;
    }
  }
  return false;
}

function label(el: HTMLElement): string {
  const cls = typeof el.className === 'string'
    ? el.className.split(/\s+/).filter((c) => c && !c.startsWith('ng-')).slice(0, 2).join('.')
    : '';
  return `${el.tagName.toLowerCase()}${cls ? '.' + cls : ''}`;
}

export function initOverflowChecker(): void {
  if (!enabled()) return;

  const panel = document.createElement('div');
  panel.style.cssText =
    'position:fixed;top:0;left:0;right:0;z-index:99999;pointer-events:none;' +
    'font:10px/1.35 monospace;background:rgba(0,0,0,.8);color:#ffb4b4;padding:4px 6px;' +
    'white-space:pre-wrap;max-height:35vh;overflow:hidden;';
  document.body.appendChild(panel);

  let marked: HTMLElement[] = [];

  const scan = () => {
    marked.forEach((m) => { m.style.outline = ''; m.style.outlineOffset = ''; });
    marked = [];
    const vw = document.documentElement.clientWidth;
    const hits: { el: HTMLElement; over: number }[] = [];
    const all = Array.from(document.body.querySelectorAll<HTMLElement>('*'));
    for (const el of all) {
      if (el === panel || panel.contains(el)) continue;
      const r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) continue;
      const over = Math.max(r.right - vw, -r.left);
      if (over <= 1) continue;
      const cs = getComputedStyle(el);
      if (cs.position === 'fixed' || cs.visibility === 'hidden') continue;
      if (containedByAncestor(el, vw)) continue;
      hits.push({ el, over: Math.round(over) });
    }
    // Keep only the outermost offenders — a too-wide card makes all its children "too wide" too.
    const set = new Set(hits.map((h) => h.el));
    const roots = hits.filter((h) => {
      for (let a = h.el.parentElement; a; a = a.parentElement) if (set.has(a)) return false;
      return true;
    });
    roots.forEach((h) => {
      h.el.style.outline = '2px solid #ff3b30';
      h.el.style.outlineOffset = '-2px';
      marked.push(h.el);
    });
    panel.textContent = roots.length
      ? `Too wide for this screen (${vw}px): ${roots.length}\n` +
        roots.slice(0, 10).map((h) => `• ${label(h.el)}  +${h.over}px`).join('\n') +
        `\n${location.pathname}`
      : `No too-wide elements ✓  (${vw}px)  ${location.pathname}`;
  };

  scan();
  setInterval(scan, 1500);
  window.addEventListener('resize', scan);
}
